import { useState } from "react";
import {
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Icon,
  IconButton,
  MenuItem,
  Tooltip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { DailyFormIssuePriority } from "generated/graphql";
import useCreateDailyFormVehicleServicePlan from "hooks/fleet/useCreateDailyFormVehicleServicePlan";
import useDeleteDailyFormVehicleServicePlan from "hooks/fleet/useDeleteDailyFormVehicleServicePlan";
import useUpdateDailyFormVehicleServicePlan from "hooks/fleet/useUpdateDailyFormVehicleServicePlan";
import useGetAllOrganizationUsersNonEmployee from "hooks/users/useGetAllOrganizationUsersNonEmployee";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";

interface VehicleServicePlansSectionProps {
  vehicleId: string;
  servicePlans: any[];
  onSaved: () => Promise<any>;
}

const intervalUnits = ["MILES", "HOURS", "DAYS"] as const;
const leadValueTooltip =
  "How early to flag or auto-create the service task before it is due. Example: 500 miles early or 14 days early.";
const selectFieldSx = {
  "& .MuiInputBase-root": {
    minHeight: "42px",
  },
};
const dateTimeLocalFormat = "YYYY-MM-DDTHH:mm";
const graphQlDateTimeFormat = "YYYY-MM-DD HH:mm:ss";

const emptyPlan = {
  id: null as string | null,
  name: "",
  intervalValue: 0,
  intervalUnit: "MILES" as const,
  leadValue: 0,
  leadUnit: "MILES" as const,
  lastCompletedAt: "",
  lastCompletedMeterReading: "",
  autoCreateTask: false,
  issueType: "Maintenance Item",
  priority: DailyFormIssuePriority.MEDIUM,
  assignedTo: "",
  notes: "",
  isActive: true,
};

const servicePlanSchema = z.object({
  id: z.string().nullable(),
  name: z.string().trim().min(1, "Plan name is required"),
  intervalValue: z.coerce.number().gt(0, "Interval value must be greater than 0"),
  intervalUnit: z.enum(["MILES", "HOURS", "DAYS"]),
  leadValue: z.coerce.number().min(0, "Lead value cannot be negative"),
  leadUnit: z.enum(["MILES", "HOURS", "DAYS"]),
  lastCompletedAt: z.string(),
  lastCompletedMeterReading: z.union([z.string(), z.number()]),
  autoCreateTask: z.boolean(),
  issueType: z.string(),
  priority: z.nativeEnum(DailyFormIssuePriority),
  assignedTo: z.string(),
  notes: z.string(),
  isActive: z.boolean(),
});

type ServicePlanFormData = z.infer<typeof servicePlanSchema>;

export default function VehicleServicePlansSection({
  vehicleId,
  servicePlans,
  onSaved,
}: VehicleServicePlansSectionProps) {
  const [createPlan] = useCreateDailyFormVehicleServicePlan();
  const [updatePlan] = useUpdateDailyFormVehicleServicePlan();
  const [deletePlan] = useDeleteDailyFormVehicleServicePlan();
  const [editingPlan, setEditingPlan] = useState<ServicePlanFormData | null>(null);
  const { data: organizationUsersData } = useGetAllOrganizationUsersNonEmployee();
  const {
    control,
    handleSubmit,
    register,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm<ServicePlanFormData>({
    resolver: zodResolver(servicePlanSchema),
    defaultValues: emptyPlan,
    mode: "onChange",
  });

  const openCreateDialog = () => {
    const nextPlan = { ...emptyPlan };
    setEditingPlan(nextPlan);
    reset(nextPlan);
    void trigger();
  };

  const openEditDialog = (plan: any) => {
    const nextPlan: ServicePlanFormData = {
      id: plan.id ?? null,
      name: plan.name ?? "",
      intervalValue: plan.intervalValue ?? emptyPlan.intervalValue,
      intervalUnit: plan.intervalUnit ?? emptyPlan.intervalUnit,
      leadValue: plan.leadValue ?? emptyPlan.leadValue,
      leadUnit: plan.leadUnit ?? plan.intervalUnit ?? emptyPlan.leadUnit,
      lastCompletedAt: plan.lastCompletedAt ? dayjs(plan.lastCompletedAt).format(dateTimeLocalFormat) : "",
      lastCompletedMeterReading: plan.lastCompletedMeterReading ?? "",
      autoCreateTask: plan.autoCreateTask ?? false,
      issueType: plan.issueType ?? emptyPlan.issueType,
      priority: plan.priority ?? emptyPlan.priority,
      assignedTo: plan.assignedTo?.id || "",
      notes: plan.notes ?? "",
      isActive: plan.isActive ?? true,
    };

    setEditingPlan(nextPlan);
    reset(nextPlan);
    void trigger();
  };

  const closeDialog = () => {
    setEditingPlan(null);
    reset({ ...emptyPlan });
    void trigger();
  };

  const handleSave: SubmitHandler<ServicePlanFormData> = async (formData) => {
    const baseInput = {
      dailyFormVehicleId: vehicleId,
      name: formData.name,
      intervalValue: Number(formData.intervalValue),
      intervalUnit: formData.intervalUnit,
      leadValue: Number(formData.leadValue || 0),
      leadUnit: formData.leadUnit || formData.intervalUnit,
      lastCompletedAt: formData.lastCompletedAt
        ? dayjs(formData.lastCompletedAt).format(graphQlDateTimeFormat)
        : null,
      lastCompletedMeterReading:
        formData.lastCompletedMeterReading === "" ? null : Number(formData.lastCompletedMeterReading),
      autoCreateTask: formData.autoCreateTask,
      issueType: formData.issueType || null,
      priority: formData.priority || null,
      assignedTo: formData.assignedTo || null,
      notes: formData.notes || null,
      isActive: formData.isActive,
    };

    if (formData.id) {
      await updatePlan({ variables: { input: { ...baseInput, id: formData.id } } });
    } else {
      await createPlan({ variables: { input: baseInput } });
    }

    closeDialog();
    await onSaved();
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <MDTypography variant="h6" fontWeight="medium">
            Recurring Service Plans
          </MDTypography>
          <MDButton color="blue" size="small" onClick={openCreateDialog}>
            <Icon>add</Icon>&nbsp;Add Plan
          </MDButton>
        </MDBox>

        {servicePlans.length === 0 ? (
          <MDTypography variant="body2" color="text">
            No recurring service plans configured.
          </MDTypography>
        ) : (
          <Grid container spacing={2}>
            {servicePlans.map((plan) => (
              <Grid item xs={12} key={plan.id}>
                <MDBox
                  px={2}
                  py={1.5}
                  border="1px solid #e0e0e0"
                  borderRadius="8px"
                  display="flex"
                  justifyContent="space-between"
                >
                  <div>
                    <MDTypography variant="button" fontWeight="medium">
                      {plan.name}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      Every {plan.intervalValue} {plan.intervalUnit} | Lead {plan.leadValue} {plan.leadUnit || plan.intervalUnit}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                      Next Due: {plan.nextDueDate || plan.nextDueMeterReading || "Not calculated"}
                    </MDTypography>
                  </div>
                  <MDBox display="flex" gap={1}>
                    <IconButton size="small" onClick={() => openEditDialog(plan)}>
                      <Icon>edit</Icon>
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deletePlan({ variables: { id: plan.id } }).then(onSaved)}
                    >
                      <Icon>delete</Icon>
                    </IconButton>
                  </MDBox>
                </MDBox>
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>

      <Dialog open={!!editingPlan} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingPlan?.id ? "Edit Service Plan" : "Add Service Plan"}</DialogTitle>
        <DialogContent>
          <MDBox
            mt={1}
            component="form"
            display="grid"
            gap={2}
            onSubmit={handleSubmit(handleSave)}
            id="vehicle-service-plan-form"
          >
            <MDBox>
              <MDInput label="Plan Name" {...register("name")} error={errors.name} />
              {errors.name && (
                <MDTypography variant="caption" color="error">
                  {errors.name.message}
                </MDTypography>
              )}
            </MDBox>
            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <MDBox>
                <Controller
                  name="intervalValue"
                  control={control}
                  render={({ field }) => (
                    <MDInput
                      {...field}
                      label="Interval Value"
                      type="number"
                      value={field.value ?? ""}
                      onChange={(e: any) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                      inputProps={{ min: 1 }}
                      error={errors.intervalValue}
                    />
                  )}
                />
                {errors.intervalValue && (
                  <MDTypography variant="caption" color="error">
                    {errors.intervalValue.message}
                  </MDTypography>
                )}
              </MDBox>
              <Controller
                name="intervalUnit"
                control={control}
                render={({ field }) => (
                  <MDInput {...field} select label="Interval Unit" fullWidth value={field.value || "MILES"} sx={selectFieldSx}>
                    {intervalUnits.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </MDInput>
                )}
              />
            </MDBox>
            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Controller
                name="leadValue"
                control={control}
                render={({ field }) => (
                  <MDInput
                    {...field}
                    label={
                      <MDBox display="flex" alignItems="center" gap={0.5}>
                        <span>Lead Value</span>
                        <Tooltip title={leadValueTooltip} placement="top" arrow>
                          <Icon fontSize="small" color="action" sx={{ cursor: "help" }}>
                            info
                          </Icon>
                        </Tooltip>
                      </MDBox>
                    }
                    type="number"
                    value={field.value ?? ""}
                    onChange={(e: any) => field.onChange(e.target.value === "" ? 0 : Number(e.target.value))}
                    inputProps={{ min: 0 }}
                  />
                )}
              />
              <Controller
                name="leadUnit"
                control={control}
                render={({ field }) => (
                  <MDInput
                    {...field}
                    select
                    label="Lead Unit"
                    fullWidth
                    value={field.value || "MILES"}
                    sx={selectFieldSx}
                  >
                    {intervalUnits.map((unit) => (
                      <MenuItem key={unit} value={unit}>
                        {unit}
                      </MenuItem>
                    ))}
                  </MDInput>
                )}
              />
            </MDBox>
            <MDBox display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Controller
                name="lastCompletedAt"
                control={control}
                render={({ field }) => (
                  <MDInput
                    {...field}
                    label="Last Completed At"
                    type="datetime-local"
                    value={field.value || ""}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              <Controller
                name="lastCompletedMeterReading"
                control={control}
                render={({ field }) => (
                  <MDInput {...field} label="Last Completed Meter" type="number" value={field.value ?? ""} />
                )}
              />
            </MDBox>
            <MDBox display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
              <MDInput label="Issue Type" {...register("issueType")} />
              <Controller
                name="priority"
                control={control}
                render={({ field }) => (
                  <MDInput
                    {...field}
                    select
                    label="Priority"
                    fullWidth
                    value={field.value || DailyFormIssuePriority.MEDIUM}
                    sx={selectFieldSx}
                  >
                    {Object.values(DailyFormIssuePriority).map((priority) => (
                      <MenuItem key={priority} value={priority}>
                        {priority}
                      </MenuItem>
                    ))}
                  </MDInput>
                )}
              />
              <Controller
                name="assignedTo"
                control={control}
                render={({ field }) => (
                  <MDInput {...field} select label="Assigned To" fullWidth value={field.value || ""} sx={selectFieldSx}>
                    <MenuItem value="">Unassigned</MenuItem>
                    {(organizationUsersData || []).map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </MDInput>
                )}
              />
            </MDBox>
            <MDInput label="Notes" multiline rows={3} {...register("notes")} />
            <Controller
              name="autoCreateTask"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Auto-create shop task"
                />
              )}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value ?? true} onChange={(e) => field.onChange(e.target.checked)} />}
                  label="Plan is active"
                />
              )}
            />
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="error" onClick={closeDialog}>
            Cancel
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            type="submit"
            form="vehicle-service-plan-form"
            disabled={!isValid}
          >
            Save
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}
